import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import type { CoachingSession } from '@efdigital/ll-addon-data';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import { array } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import { ResultsTable } from '../../results-table.gts';

interface CoachingSessionsTableSignature {
  Args: {
    sessions: CoachingSession[];
  };
  Element: HTMLTableElement;
}

function toDate(date: string): Date {
  return new Date(date);
}

export class CoachingSessionsTable extends Component<CoachingSessionsTableSignature> {
  @service declare store: Store;
  @service declare userLookup: UserLookupService;

  lookupUsers = async () => {
    await this.userLookup.lookupUsers([
      ...this.args.sessions.map((session) => session.coachUserId),
      ...this.args.sessions.map((session) => session.user),
    ]);
  };

  <template>
    <Await @promise={{(this.lookupUsers)}}>
      <:success>
        <ResultsTable @data={{@sessions}} ...attributes>
          <:header>
            <th scope="col">Actions</th>
            <th scope="col">Coach</th>
            <th scope="col">Coachee</th>
            <th scope="col">Status</th>
            <th scope="col">Date</th>
            <th scope="col">Time</th>
          </:header>
          <:row as |session|>
            <tr>
              <td>
                <LinkTo
                  @route="coaching.sessions.session"
                  @models={{array session.id}}
                >
                  View
                </LinkTo>
              </td>
              {{#let
                (this.userLookup.fromCache session.coachUserId)
                as |coach|
              }}
                <td>{{userDisplayName coach}}</td>
              {{/let}}
              {{#let (this.userLookup.fromCache session.user) as |coachee|}}
                <td>{{userDisplayName coachee}}</td>
              {{/let}}
              <td>
                {{#let
                  (getSessionStatusDisplay session.status)
                  as |statusDisplay|
                }}
                  <span
                    class="badge rounded-pill bg-{{statusDisplay.color}}-subtle text-{{statusDisplay.color}}-emphasis fw-semibold px-2 py-1 d-flex align-items-center gap-1"
                    title={{statusDisplay.description}}
                  >
                    <iconify-icon
                      inline
                      icon={{statusDisplay.icon}}
                      aria-hidden="true"
                    />
                    {{statusDisplay.uniqueLabel}}
                  </span>
                {{/let}}
              </td>
              <td>
                {{formatDate (toDate session.startTime)}}
              </td>
              <td>
                {{formatTimeRange
                  (toDate session.startTime)
                  (toDate session.endTime)
                }}
              </td>
            </tr>
          </:row>
          <:empty>
            <div class="alert alert-info" role="alert">No sessions found
              matching your search criteria.</div>
          </:empty>
        </ResultsTable>
      </:success>
    </Await>
  </template>
}
